import {
  INVALID_COMPANY_LOGO_FORMAT_ERROR_MESSAGE,
  putCompanyLogoFileToS3,
  resolveCompanyLogoContentType,
  useInnovatorCompleteCompanyLogoUpload,
  useInnovatorDeleteCompanyLogo,
  useInnovatorRequestCompanyLogoUploadUrl,
  validateCompanyLogoFile,
} from '@queries/company-logo.query';
import { useCallback, useEffect, useState } from 'react';

type CompanyLogoDraftState = {
  file?: File;
  isDeleteRequested: boolean;
};

type UseCompanyProfileModalLogoParams = {
  existingCompanyLogoUrl?: string;
};

export const useCompanyProfileModalLogo = (params: UseCompanyProfileModalLogoParams) => {
  const { existingCompanyLogoUrl } = params;
  const [companyLogoDraft, setCompanyLogoDraft] = useState<CompanyLogoDraftState>({
    isDeleteRequested: false,
  });
  const [companyLogoError, setCompanyLogoError] = useState<string | undefined>(undefined);
  const [isCompanyLogoUploadPending, setIsCompanyLogoUploadPending] = useState(false);
  const [companyLogoObjectUrl, setCompanyLogoObjectUrl] = useState<string | undefined>(undefined);

  const { isPending: completeCompanyLogoUploadPending, mutateAsync: completeCompanyLogoUpload } =
    useInnovatorCompleteCompanyLogoUpload();
  const { isPending: deleteCompanyLogoPending, mutateAsync: deleteCompanyLogo } = useInnovatorDeleteCompanyLogo();
  const { isPending: requestCompanyLogoUploadUrlPending, mutateAsync: requestCompanyLogoUploadUrl } =
    useInnovatorRequestCompanyLogoUploadUrl();

  useEffect(() => {
    if (!companyLogoDraft.file) {
      setCompanyLogoObjectUrl(undefined);
      return;
    }

    const url = URL.createObjectURL(companyLogoDraft.file);
    setCompanyLogoObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [companyLogoDraft.file]);

  const shouldShowExistingCompanyLogo = Boolean(existingCompanyLogoUrl && !companyLogoDraft.isDeleteRequested);
  const companyLogoPreviewUrl =
    companyLogoObjectUrl || (shouldShowExistingCompanyLogo ? existingCompanyLogoUrl : undefined);

  const resetCompanyLogoDraft = useCallback(() => {
    setCompanyLogoDraft({ isDeleteRequested: false });
    setCompanyLogoError(undefined);
  }, []);

  const handleCompanyLogoFileSelect = useCallback((file: File | undefined) => {
    if (!file) {
      return;
    }

    const validationError = validateCompanyLogoFile(file);
    if (validationError) {
      setCompanyLogoError(validationError);
      return;
    }

    setCompanyLogoDraft({
      file,
      isDeleteRequested: false,
    });
    setCompanyLogoError(undefined);
  }, []);

  const handleCompanyLogoRemove = useCallback(() => {
    setCompanyLogoDraft((previousDraft) => {
      if (previousDraft.file) {
        return { isDeleteRequested: false };
      }

      if (existingCompanyLogoUrl) {
        return { isDeleteRequested: true };
      }

      return { isDeleteRequested: false };
    });
    setCompanyLogoError(undefined);
  }, [existingCompanyLogoUrl]);

  const handleCompanyLogoRestore = useCallback(() => {
    setCompanyLogoDraft((previousDraft) => ({
      ...previousDraft,
      isDeleteRequested: false,
    }));
    setCompanyLogoError(undefined);
  }, []);

  const applyCompanyLogoDraft = useCallback(async (): Promise<void> => {
    const file = companyLogoDraft.file;

    if (file) {
      const contentType = resolveCompanyLogoContentType(file.type);
      if (!contentType) {
        throw new Error(INVALID_COMPANY_LOGO_FORMAT_ERROR_MESSAGE);
      }

      setIsCompanyLogoUploadPending(true);

      try {
        const uploadResult = await requestCompanyLogoUploadUrl({
          contentType,
          size: file.size,
          title: file.name,
        });

        await putCompanyLogoFileToS3({
          contentType,
          file,
          uploadUrl: uploadResult.uploadUrl,
        });

        await completeCompanyLogoUpload({
          attachmentId: uploadResult.attachmentId,
        });
      } finally {
        setIsCompanyLogoUploadPending(false);
      }

      return;
    }

    if (companyLogoDraft.isDeleteRequested && existingCompanyLogoUrl) {
      await deleteCompanyLogo();
    }
  }, [
    companyLogoDraft.file,
    companyLogoDraft.isDeleteRequested,
    completeCompanyLogoUpload,
    deleteCompanyLogo,
    existingCompanyLogoUrl,
    requestCompanyLogoUploadUrl,
  ]);

  return {
    applyCompanyLogoDraft,
    companyLogoError,
    companyLogoPreviewUrl,
    handleCompanyLogoFileSelect,
    handleCompanyLogoRemove,
    handleCompanyLogoRestore,
    isCompanyLogoDeleteRequested: companyLogoDraft.isDeleteRequested,
    isCompanyLogoMutationPending:
      completeCompanyLogoUploadPending ||
      deleteCompanyLogoPending ||
      isCompanyLogoUploadPending ||
      requestCompanyLogoUploadUrlPending,
    resetCompanyLogoDraft,
  };
};
